/**
 * Política unificada de retry: attempts, backoff, budget, cooldown.
 */

import type { QueueItem, QueueItemType } from '../queue/types';
import { classifyRetry } from './retryClassifier';
import { canSpendRetryBudget, spendRetryBudget } from './retryBudget';
import { getCooldownRemaining, recordFailureCooldown } from './retryCooldown';
import { getQueuePartition } from '../queue/partitioning';
import {
  canSpendPartitionRetryBudget,
  spendPartitionRetryBudget,
} from '../queue/partitionPolicies';
import { getUnifiedPressureLevel } from '../runtime/pressureModel';
import { getRuntimeMode } from '../runtime/runtimeMode';

const MAX_ATTEMPTS_BY_TYPE: Record<QueueItemType, number> = {
  conversion: 5,
  pageview: 4,
  behavior: 3,
};

const MAX_ATTEMPTS_BY_EVENT: Record<string, number> = {
  PURCHASE: 8,
  LEAD: 8,
  INITIATE_CHECKOUT: 7,
  ADD_PAYMENT_INFO: 7,
  SUBSCRIPTION: 7,
  BEHAVIOR_SUMMARY: 2,
  HEARTBEAT: 2,
  SCROLL: 2,
};

const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 60_000;

export function getMaxAttemptsForItem(item: QueueItem): number {
  return (
    MAX_ATTEMPTS_BY_EVENT[item.params.type] ?? MAX_ATTEMPTS_BY_TYPE[item.type]
  );
}

function getBackoffMs(attempt: number, retryClass: ReturnType<typeof classifyRetry>): number {
  if (attempt <= 1) return 0;
  const pressure = getUnifiedPressureLevel();
  const mode = getRuntimeMode();
  const pressureMul = 1 + pressure * 0.5;
  const degradedMul = mode === 'degraded' && retryClass === 'low' ? 2 : 1;
  const multiplier =
    (retryClass === 'critical' ? 0.75 : retryClass === 'medium' ? 1 : 1.25) *
    pressureMul *
    degradedMul;
  const raw = Math.min(
    BACKOFF_BASE_MS * multiplier * Math.pow(2, attempt - 1),
    BACKOFF_MAX_MS
  );
  const jitter = raw * 0.1 * (Math.random() - 0.5);
  return Math.max(0, Math.round(raw + jitter));
}

export function canRetryItem(item: QueueItem, now: number): boolean {
  const nextAttempt = item.attempts + 1;
  if (nextAttempt >= getMaxAttemptsForItem(item)) return false;
  const retryClass = classifyRetry(item.params.type);
  if (!canSpendRetryBudget(retryClass, now)) return false;
  const partition = getQueuePartition(item);
  if (!canSpendPartitionRetryBudget(partition, now)) return false;
  if (getCooldownRemaining(item.params.type, now) > 0) return false;
  return true;
}

export function scheduleRetry(item: QueueItem, now: number): number {
  const retryClass = classifyRetry(item.params.type);
  spendRetryBudget(retryClass);
  spendPartitionRetryBudget(getQueuePartition(item));
  recordFailureCooldown(item.params.type);
  const nextAttempt = item.attempts + 1;
  return now + getBackoffMs(nextAttempt, retryClass);
}
